import { Alert, Badge, Button, Card, CardHeader, Field, PageHeader, PasswordInput } from '@dnx/ui';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { KeyRound, LogOut, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';

export function AccountPage() {
  const { data: me } = useSuspenseQuery(meQuery);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const logoutAll = useMutation({
    mutationFn: () => api<void>('/auth/logout-all', { method: 'POST' }),
    onSuccess: () => { qc.clear(); void navigate({ to: '/entrar' }); },
  });

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Meu perfil" description={<>{me.user.name} · {me.user.email}</>} />

      <Card className="mt-8">
        <CardHeader title="Verificação em duas etapas" description="Obrigatória para toda a equipe que acessa dados de pacientes."
          action={<Badge tone="success"><ShieldCheck className="size-3" aria-hidden />Ativa</Badge>} />
        <p className="px-5 pt-3 pb-5 text-[13px] text-fg-muted">
          Perdeu o celular? Entre com um código de recuperação e peça ao administrador para reconfigurar.
        </p>
      </Card>

      <ChangePasswordCard />

      <Card className="mt-5">
        <CardHeader title="Sessões" description="Encerre o acesso em todos os computadores e celulares — útil se você esqueceu a conta aberta." />
        <div className="px-5 pt-4 pb-5">
          {!confirming ? (
            <Button variant="secondary" onClick={() => setConfirming(true)}><LogOut className="size-4" />Sair de todos os dispositivos</Button>
          ) : (
            <Alert tone="warning" title="Encerrar todas as sessões?">
              <p>Você e qualquer outro dispositivo conectado precisarão entrar de novo com senha e código.</p>
              <div className="mt-3 flex gap-2">
                <Button variant="danger" size="sm" loading={logoutAll.isPending} onClick={() => logoutAll.mutate()}>Encerrar todas</Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>Cancelar</Button>
              </div>
            </Alert>
          )}
        </div>
      </Card>
    </div>
  );
}

/** Troca de senha: confere a atual; os outros computadores e celulares saem, este continua. */
function ChangePasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);
  const change = useMutation({
    mutationFn: () => api<void>('/auth/password/change', { method: 'POST', body: { currentPassword: current, newPassword: next } }),
    onSuccess: () => { setCurrent(''); setNext(''); setConfirm(''); setTouched(false); },
  });
  const short = next.length < 12;
  const mismatch = next !== confirm;
  return (
    <Card className="mt-5">
      <CardHeader title="Senha" description="Troque sempre que suspeitar que alguém viu. Os outros aparelhos conectados saem; este continua." />
      <form className="grid gap-4 px-5 pt-4 pb-5 sm:grid-cols-3" noValidate
        onSubmit={(e) => { e.preventDefault(); setTouched(true); if (current && !short && !mismatch) change.mutate(); }}>
        <Field label="Senha atual" error={touched && !current ? 'Informe a senha atual' : undefined}>
          <PasswordInput autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
        </Field>
        <Field label="Nova senha" error={touched && short ? 'Pelo menos 12 caracteres' : undefined}>
          <PasswordInput autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
        </Field>
        <Field label="Repita a nova" error={touched && mismatch ? 'Não confere' : undefined}>
          <PasswordInput autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <div className="flex flex-wrap items-center gap-3 sm:col-span-3">
          <Button type="submit" variant="secondary" loading={change.isPending}><KeyRound className="size-4" />Alterar senha</Button>
          {change.isSuccess && <span className="text-sm text-success">Senha alterada.</span>}
        </div>
        {change.error && <div className="sm:col-span-3"><Alert tone="danger">{change.error.message}</Alert></div>}
      </form>
    </Card>
  );
}
